import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import FileInput from "@/components/ui/file-input";
import "./index.scss";

export interface DocumentItem {
  id: string;
  name: string;
  file?: File;
  /** Backend'e yüklendikten sonra dönen dosya ID – documentFileIDs'de gönderilir */
  fileID?: string;
  isVisibleToTrainee: boolean;
}

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (document: DocumentItem) => void;
  editingDocument?: DocumentItem | null;
  supportedFormats?: string[];
  maxSize?: number; // in MB
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingDocument = null,
  // PPT/PPTX backend'in MIME + uzantı allowlist'inde zaten kabul ediliyordu
  // (`application/vnd.openxmlformats-officedocument…`, `.ppt`, `.pptx`), yalnız bu
  // varsayılan liste dışında kaldığı için istemci sunum dosyalarını reddediyordu.
  supportedFormats = ["PDF", "DOC", "DOCX", "XLS", "XLSX", "PPT", "PPTX"],
  // Backend'de video dışı dosyalar için sınır 10MB (FilesService.MAX_FILE_SIZE);
  // 500 yazmak kullanıcıyı sunucunun reddedeceği bir yüklemeye yönlendiriyordu.
  maxSize = 10,
}) => {
  const { t } = useTranslation();
  const [documentName, setDocumentName] = useState("");
  const [uploadedFileID, setUploadedFileID] = useState<string | null>(null);
  const [isVisibleToTrainee, setIsVisibleToTrainee] = useState(true);

  React.useEffect(() => {
    if (isOpen && editingDocument) {
      setDocumentName(editingDocument.name);
      setUploadedFileID(editingDocument.fileID ?? null);
      setIsVisibleToTrainee(editingDocument.isVisibleToTrainee);
    } else if (isOpen) {
      setDocumentName("");
      setUploadedFileID(null);
      setIsVisibleToTrainee(true);
    }
  }, [isOpen, editingDocument]);

  const resetForm = () => {
    setDocumentName("");
    setUploadedFileID(null);
    setIsVisibleToTrainee(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleUploadSuccess = (
    fileId: string,
    _previewUrl?: string,
    meta?: { fileName?: string; mimeType?: string },
  ) => {
    setUploadedFileID(fileId);
    // Backend bu form için ayrı dosya adı ve görünürlük alanı kabul etmiyor;
    // sadece documentFileIDs gönderiliyor. Bu nedenle kullanıcının eli boş
    // kalmasın diye dosya adını dosyanın orijinal adıyla otomatik dolduruyoruz.
    if (meta?.fileName && !documentName.trim()) {
      setDocumentName(meta.fileName);
    }
  };

  const handleFileRemove = () => {
    setUploadedFileID(null);
  };

  const handleSubmit = () => {
    const fileID = uploadedFileID ?? editingDocument?.fileID;
    if (!documentName.trim() || !fileID) {
      return;
    }

    const document: DocumentItem = {
      id: editingDocument?.id ?? `doc-${Date.now()}`,
      name: documentName.trim(),
      fileID,
      isVisibleToTrainee,
    };

    onSubmit(document);
    handleClose();
  };

  const isFormValid = documentName.trim() !== "" && (!!uploadedFileID || !!editingDocument?.fileID);

  const customFooter = (
    <div className="document-upload-modal-footer">
      <Button variant="text" color="secondary" onClick={handleClose}>
        {t("createEducation.documentModal.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        {t("createEducation.documentModal.addDocument")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("createEducation.documentModal.title")}
      size="lg"
      customFooter={customFooter}
    >
      <div className="document-upload-modal-content">
        {/* Document Name Input */}
        <div className="form-field">
          <Input
            label={
              t("createEducation.documentModal.documentName") + "*"
            }
            placeholder={t("createEducation.documentModal.documentNamePlaceholder")}
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            required
          />
        </div>

        {/* File Upload Area – backend'e yüklenir, fileID documentFileIDs'de gönderilir */}
        <FileInput
          label={t("createEducation.documentModal.uploadDocument") + "*"}
          placeholder={t("createEducation.documentModal.dropzoneText")}
          accept={supportedFormats.map((f) => `.${f.toLowerCase()}`).join(",")}
          supportedFormats={supportedFormats}
          maxSize={maxSize}
          maxFiles={1}
          uploadToBackend
          onUploadSuccess={handleUploadSuccess}
          onFileRemove={handleFileRemove}
          enableDemoProgress={false}
          fileType="file"
        />

        {/*
          t("documentUpload.traineeVisibility") switch'i şimdilik kaldırıldı: backend
          documentFileIDs dışında alan kabul etmediği için seçim payload'a
          gitmiyor ve kullanıcıya yanlış izlenim veriyordu. Backend desteği
          (per-document metadata) eklendiğinde geri alınacak.
        */}
      </div>
    </Modal>
  );
};

export default DocumentUploadModal;
